import React, { useState, useEffect } from 'react';
import { Brain, Trash2, CheckCircle2, RefreshCw, Search, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  getKbSnapshot,
  deleteTermMapping,
  deletePattern,
  approvePattern,
  updateRiskScoringRules,
  createRiskTableDict,
  deleteRiskTableDict,
  createRiskMetric,
  deleteRiskMetric,
  createRiskExample,
  deleteRiskExample,
} from '../services/api';

const formatRelativeTime = (iso) => {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return 'just now';
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
    return `${Math.floor(diff / 86400_000)}d ago`;
  } catch {
    return iso;
  }
};

export default function LearningsInspectorPage() {
  const [snapshot, setSnapshot] = useState({
    term_mappings: { items: [], count: 0 },
    patterns: { items: [], count: 0, approved: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchPattern, setSearchPattern] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await getKbSnapshot();
      setSnapshot(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load knowledge base snapshot');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDeleteMapping = async (id, term) => {
    if (!window.confirm(`Delete term mapping "${term}"?`)) return;
    try {
      await deleteTermMapping(id);
      toast.success(`Deleted mapping "${term}"`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDeletePattern = async (id) => {
    if (!window.confirm('Delete this learned pattern?')) return;
    try {
      await deletePattern(id);
      toast.success('Pattern deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleApprovePattern = async (id) => {
    try {
      await approvePattern(id);
      toast.success('Pattern marked admin-approved');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    }
  };

  const filteredMappings = (snapshot.term_mappings?.items || []).filter(m => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (m.term || '').toLowerCase().includes(s) ||
      (m.table || '').toLowerCase().includes(s) ||
      (m.database || '').toLowerCase().includes(s) ||
      (m.server || '').toLowerCase().includes(s) ||
      (m.phrases || []).some(p => (p || '').toLowerCase().includes(s))
    );
  });

  const filteredPatterns = (snapshot.patterns?.items || []).filter(p => {
    if (!searchPattern) return true;
    const s = searchPattern.toLowerCase();
    return (
      (p.question_original || '').toLowerCase().includes(s) ||
      (p.sql || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="learnings-inspector-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 text-gray-900">
            <Brain className="w-8 h-8 text-[#D4AF37]" />
            Learnings Inspector
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Everything the AI has learned about your business — term mappings and successful query patterns.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          disabled={loading}
          data-testid="learnings-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
          <div className="text-xs uppercase tracking-wide text-blue-700 mb-1">Term Mappings</div>
          <div className="text-3xl font-bold text-blue-900" data-testid="stat-term-count">
            {snapshot.term_mappings?.count ?? 0}
          </div>
          <div className="text-xs text-blue-700 mt-1">Business term → table</div>
        </div>
        <div className="p-5 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-lg">
          <div className="text-xs uppercase tracking-wide text-emerald-700 mb-1">Learned Patterns</div>
          <div className="text-3xl font-bold text-emerald-900" data-testid="stat-pattern-count">
            {snapshot.patterns?.count ?? 0}
          </div>
          <div className="text-xs text-emerald-700 mt-1">
            {snapshot.patterns?.approved ?? 0} admin-approved
          </div>
        </div>
        <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
          <div className="text-xs uppercase tracking-wide text-amber-700 mb-1">Active Learning</div>
          <div className="text-3xl font-bold text-amber-900" data-testid="stat-active-learning">
            {((snapshot.term_mappings?.count ?? 0) + (snapshot.patterns?.count ?? 0))}
          </div>
          <div className="text-xs text-amber-700 mt-1">Total knowledge entries</div>
        </div>
      </div>

      <Tabs defaultValue="terms" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="terms" data-testid="tab-terms">
            Term Mappings ({snapshot.term_mappings?.count ?? 0})
          </TabsTrigger>
          <TabsTrigger value="patterns" data-testid="tab-patterns">
            Pattern Library ({snapshot.patterns?.count ?? 0})
          </TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk">
            Risk Framework 🛡️
          </TabsTrigger>
        </TabsList>

        {/* TERM MAPPINGS TAB */}
        <TabsContent value="terms">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200 flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by term, table, database, server, phrase…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-0 focus-visible:ring-0 shadow-none"
                data-testid="search-term-input"
              />
            </div>

            {filteredMappings.length === 0 ? (
              <div className="p-10 text-center text-gray-500 text-sm">
                {snapshot.term_mappings?.count === 0
                  ? <>No term mappings yet. When the AI asks <strong>"Which table did you mean?"</strong>, your answer is saved here.</>
                  : 'No mappings match your search.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredMappings.map((m) => (
                  <div
                    key={m.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`term-mapping-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold text-sm"
                            data-testid={`term-label-${m.id}`}
                          >
                            {m.term}
                          </span>
                          <span className="text-gray-400">→</span>
                          <code className="text-sm text-gray-800">
                            <span className="text-blue-600">{m.server}</span>
                            <span className="text-gray-400">.</span>
                            <span className="text-purple-600">{m.database}</span>
                            <span className="text-gray-400">.</span>
                            <span className="font-semibold">{m.table}</span>
                          </code>
                          <Badge variant="outline" className="text-xs">
                            used {m.usage_count ?? 0}×
                          </Badge>
                        </div>
                        {m.filter && (
                          <div className="text-xs text-gray-600 mb-1 font-mono">
                            WHERE <span className="text-gray-900">{m.filter}</span>
                          </div>
                        )}
                        {(m.phrases || []).length > 1 && (
                          <div className="text-xs text-gray-500">
                            Also matches: {(m.phrases || []).filter(p => p !== m.term).join(', ')}
                          </div>
                        )}
                        {m.notes && (
                          <div className="text-xs text-gray-500 italic mt-1">{m.notes}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          Created {formatRelativeTime(m.created_at)}
                          {m.last_used_at && <> · last used {formatRelativeTime(m.last_used_at)}</>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMapping(m.id, m.term)}
                        className="text-red-600 hover:bg-red-50 shrink-0"
                        data-testid={`delete-term-${m.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* PATTERN LIBRARY TAB */}
        <TabsContent value="patterns">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200 flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by question or SQL fragment…"
                value={searchPattern}
                onChange={(e) => setSearchPattern(e.target.value)}
                className="border-0 focus-visible:ring-0 shadow-none"
                data-testid="search-pattern-input"
              />
            </div>

            {filteredPatterns.length === 0 ? (
              <div className="p-10 text-center text-gray-500 text-sm">
                {snapshot.patterns?.count === 0 ? (
                  <div className="flex flex-col items-center gap-2">
                    <AlertTriangle className="w-8 h-8 text-amber-400" />
                    <div>No patterns yet — successful queries are auto-saved here.</div>
                    <div className="text-xs">Ask a question that returns data, and you'll see it appear.</div>
                  </div>
                ) : 'No patterns match your search.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredPatterns.map((p) => (
                  <div
                    key={p.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`pattern-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-medium text-gray-900 text-sm">
                            "{p.question_original}"
                          </span>
                          {p.is_admin_approved && (
                            <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> approved
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            used {p.usage_count ?? 0}×
                          </Badge>
                          {p.row_count > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {p.row_count} rows
                            </Badge>
                          )}
                        </div>
                        {(p.selected_tables?.length || p.tables_used?.length) > 0 && (
                          <div className="text-xs text-gray-500 mb-1">
                            Tables: {(p.selected_tables || p.tables_used || []).join(', ')}
                          </div>
                        )}
                        <pre className="text-xs bg-gray-50 rounded p-2 border border-gray-100 overflow-x-auto text-gray-700 max-h-32">
                          {(p.sql || '').slice(0, 600)}{(p.sql || '').length > 600 ? '…' : ''}
                        </pre>
                        <div className="text-xs text-gray-400 mt-1">
                          Created {formatRelativeTime(p.created_at)}
                          {p.last_used_at && <> · last used {formatRelativeTime(p.last_used_at)}</>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {!p.is_admin_approved && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprovePattern(p.id)}
                            className="text-emerald-700 hover:bg-emerald-50"
                            data-testid={`approve-pattern-${p.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePattern(p.id)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-pattern-${p.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* RISK FRAMEWORK TAB */}
        <TabsContent value="risk">
          <RiskFrameworkTab framework={snapshot.risk_framework || {}} onReload={load} />
        </TabsContent>

      </Tabs>
    </div>
  );
}


// ============================================
// RISK FRAMEWORK TAB
// ============================================
function RiskFrameworkTab({ framework, onReload }) {
  const rules = framework.scoring_rules || {};
  const weights = rules.weights || {};
  const thresholds = rules.thresholds || {};
  const hardRules = rules.hard_rules || [];
  const tableDict = framework.table_dict || [];
  const metrics = framework.metrics || [];
  const examples = framework.examples || [];

  const [editingWeights, setEditingWeights] = useState(false);
  const [draftWeights, setDraftWeights] = useState(weights);

  useEffect(() => {
    setDraftWeights(weights);
  }, [JSON.stringify(weights)]); // eslint-disable-line react-hooks/exhaustive-deps

  const weightSum = Object.values(draftWeights || {}).reduce((a, b) => a + Number(b || 0), 0);

  const saveWeights = async () => {
    try {
      await updateRiskScoringRules({ weights: draftWeights });
      toast.success('Scoring weights updated');
      setEditingWeights(false);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const addTableDict = async () => {
    const tableName = window.prompt('Table name (e.g. Wagers, payment_txns):');
    if (!tableName) return;
    const purpose = window.prompt(`Purpose of '${tableName}':`) || '';
    const join_key = window.prompt(`Join key:`) || '';
    try {
      await createRiskTableDict({ table: tableName, purpose, join_key });
      toast.success(`Added table '${tableName}'`);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Add failed');
    }
  };

  const removeTableDict = async (id, name) => {
    if (!window.confirm(`Delete '${name}'?`)) return;
    await deleteRiskTableDict(id);
    toast.success('Deleted');
    onReload && onReload();
  };

  const addMetric = async () => {
    const name = window.prompt('Metric name:');
    if (!name) return;
    const formula = window.prompt('SQL formula:') || '';
    const business_meaning = window.prompt('Business meaning:') || '';
    await createRiskMetric({ name, formula, business_meaning });
    toast.success(`Added metric '${name}'`);
    onReload && onReload();
  };

  const removeMetric = async (id, name) => {
    if (!window.confirm(`Delete metric '${name}'?`)) return;
    await deleteRiskMetric(id);
    toast.success('Deleted');
    onReload && onReload();
  };

  const addExample = async () => {
    const name = window.prompt('Example name:');
    if (!name) return;
    const label = window.prompt('Label (low/medium/high/critical/inconclusive):') || 'medium';
    const input_summary = window.prompt('Input shape summary:') || '';
    const output_template = window.prompt('Expected output template:') || '';
    await createRiskExample({ name, label, input_summary, output_template });
    toast.success(`Added example '${name}'`);
    onReload && onReload();
  };

  const removeExample = async (id, name) => {
    if (!window.confirm(`Delete example '${name}'?`)) return;
    await deleteRiskExample(id);
    toast.success('Deleted');
    onReload && onReload();
  };

  return (
    <div className="space-y-6" data-testid="risk-framework-tab">
      {/* Scoring weights */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            🎯 Area weights <span className="text-xs text-gray-500">(sum = {weightSum}%)</span>
          </h3>
          {!editingWeights ? (
            <Button size="sm" variant="outline" onClick={() => setEditingWeights(true)} data-testid="edit-weights-btn">
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setDraftWeights(weights); setEditingWeights(false); }}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={weightSum !== 100}
                onClick={saveWeights}
                data-testid="save-weights-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Save{weightSum !== 100 ? ` (sum must =100, got ${weightSum})` : ''}
              </Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(draftWeights || {}).map(([area, val]) => (
            <div key={area} className="flex items-center gap-2">
              <label className="text-sm text-gray-700 flex-1">{area.replace(/_/g, ' ')}</label>
              {editingWeights ? (
                <Input
                  type="number"
                  className="w-20"
                  value={val}
                  onChange={(e) => setDraftWeights(prev => ({ ...prev, [area]: Number(e.target.value || 0) }))}
                  data-testid={`weight-${area}`}
                />
              ) : (
                <span className="font-mono text-sm font-semibold text-blue-700">{val}%</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Thresholds */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Score band thresholds</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(thresholds).map(([band, range]) => (
            <div key={band} className="p-3 rounded border border-gray-100 bg-gray-50">
              <div className="text-xs uppercase text-gray-500">{band}</div>
              <div className="text-lg font-mono font-bold">
                {Array.isArray(range) ? `${range[0]}–${range[1]}` : range}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hard rules */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">📜 Hard rules (non-negotiable)</h3>
        <ul className="space-y-2 list-disc list-inside text-sm text-gray-700">
          {hardRules.map((rule, i) => (
            <li key={i}>{rule}</li>
          ))}
        </ul>
      </div>

      {/* Table dictionary */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">📚 Risk table dictionary</h3>
          <Button size="sm" variant="outline" onClick={addTableDict} data-testid="add-table-dict-btn">+ Add</Button>
        </div>
        <div className="space-y-2">
          {tableDict.map((t) => (
            <div key={t.id} className="p-3 rounded border border-gray-100 hover:bg-gray-50 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm font-semibold text-gray-900">{t.table}</div>
                <div className="text-xs text-gray-600 mt-0.5">{t.purpose}</div>
                <div className="text-xs text-gray-500 mt-1">
                  <strong>join:</strong> {t.join_key || '—'}
                  {t.data_quality_notes && <> · <em>{t.data_quality_notes}</em></>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeTableDict(t.id, t.table)} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">📐 Metric definitions</h3>
          <Button size="sm" variant="outline" onClick={addMetric} data-testid="add-metric-btn">+ Add</Button>
        </div>
        <div className="space-y-2">
          {metrics.map((m) => (
            <div key={m.id} className="p-3 rounded border border-gray-100 hover:bg-gray-50 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">{m.name}</div>
                <code className="block text-xs text-purple-700 mt-1">{m.formula}</code>
                <div className="text-xs text-gray-600 mt-1">{m.business_meaning}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeMetric(m.id, m.name)} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Examples */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">📋 Worked examples (few-shot)</h3>
          <Button size="sm" variant="outline" onClick={addExample} data-testid="add-example-btn">+ Add</Button>
        </div>
        <div className="space-y-2">
          {examples.map((ex) => (
            <div key={ex.id} className="p-3 rounded border border-gray-100 hover:bg-gray-50 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{ex.name}</span>
                  <Badge variant="outline" className="text-xs uppercase">{ex.label}</Badge>
                </div>
                <div className="text-xs text-gray-600 mt-1"><strong>Input:</strong> {ex.input_summary}</div>
                <div className="text-xs text-gray-500 mt-1"><strong>Output:</strong> {ex.output_template}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeExample(ex.id, ex.name)} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
